import type { EditorContext, EditorRegion } from "@loumy/contracts";
import { useState } from "react";
import type { WebEditorPanelProps, WebPanelDescriptor } from "../registry/editorRegistry";
import { cx, Icon } from "../ui";

// D66 — rendert die Panels EINER Region. Mehrere Panels je Region werden als ECHTE Tabs gezeigt
// (D102 — der Header trägt die Tab-Leiste, gerendert wird nur das aktive Panel; z.B. rechts
// „Details | Logik"). Collapse/Expand über das vorhandene chevron-Icon; im collapsed-Zustand
// bleibt nur eine schmale Klick-Leiste zum Wiederaufklappen. Der Viewport ist nie collapsible
// und füllt (fill) die Fläche randlos (das Panel spannt selbst `<canvas>`/WebGL/DOM auf —
// Shell ist Backend-agnostisch).

/** Breite/Höhe einer kollabierten Region (schmale Reaktivierungs-Leiste). */
export const COLLAPSED_PX = 34;

const REGION_LABELS: Record<EditorRegion, string> = {
  toolbar: "Toolbar",
  tools: "Werkzeuge",
  viewport: "Viewport",
  outliner: "Outliner",
  details: "Details",
};

interface RegionContainerProps {
  region: EditorRegion;
  panels: WebPanelDescriptor[];
  ctx: EditorContext;
  collapsed: boolean;
  collapsible: boolean;
  onToggle: () => void;
  /** Achse, entlang derer die Region kollabiert: "width" = Spalte (tools), "height" = Zeile. */
  orientation: "width" | "height";
  /** Viewport-Modus: randloser, nicht scrollender Body (Canvas füllt). */
  fill?: boolean;
  /** Header ausblenden (z.B. Viewport für maximale Fläche). */
  headerless?: boolean;
  /** Overflow erlauben (z.B. Toolbar/Menüleiste: Dropdown-Menüs dürfen herausragen). */
  allowOverflow?: boolean;
}

export function RegionContainer({
  region,
  panels,
  ctx,
  collapsed,
  collapsible,
  onToggle,
  orientation,
  fill,
  headerless,
  allowOverflow,
}: RegionContainerProps) {
  const title = panels.map((p) => p.title).join(" · ") || REGION_LABELS[region];
  const [activeId, setActiveId] = useState<string | undefined>(undefined);

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onToggle}
        title={`${title} einblenden`}
        className={cx(
          "flex h-full w-full items-center justify-center gap-1.5 bg-surface text-muted hover:text-text",
          orientation === "width" ? "flex-col py-2" : "px-2.5",
        )}
      >
        <Icon name="chevron-right" size={14} />
        <span
          className={cx(
            "text-[10px] font-medium uppercase tracking-wide",
            orientation === "width" && "[writing-mode:vertical-rl] rotate-180",
          )}
        >
          {title}
        </span>
      </button>
    );
  }

  // D102 — Tab-Zustand (nur relevant bei >1 Panel): ungültige/fehlende Wahl fällt auf das erste Panel
  // zurück (Editor-Wechsel ändert die Panel-Menge — kein stale Tab).
  const tabbed = panels.length > 1;
  const active = (activeId !== undefined && panels.find((p) => p.id === activeId)) || panels[0];
  const visible = tabbed && active ? [active] : panels;

  return (
    <div className={cx("flex h-full w-full flex-col bg-surface", allowOverflow ? "overflow-visible" : "overflow-hidden")}>
      {!headerless && (
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-2.5 py-1.5">
          {tabbed ? (
            <div className="flex min-w-0 items-center gap-1">
              {panels.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setActiveId(p.id)}
                  className={cx(
                    "truncate rounded px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide transition-colors",
                    p.id === active?.id ? "bg-accent/20 text-accent" : "text-muted hover:text-text",
                  )}
                >
                  {p.title}
                </button>
              ))}
            </div>
          ) : (
            <span className="truncate text-[11px] font-medium uppercase tracking-wide text-muted">{title}</span>
          )}
          {collapsible && (
            <button
              type="button"
              onClick={onToggle}
              title={`${title} ausblenden`}
              className="grid h-5 w-5 place-items-center rounded text-muted hover:bg-surface-2 hover:text-text"
              aria-label={`${title} ausblenden`}
            >
              <Icon name="chevron-down" size={14} />
            </button>
          )}
        </div>
      )}
      <div className={cx("min-h-0 flex-1", fill ? "relative overflow-hidden" : allowOverflow ? "overflow-visible" : "overflow-auto")}>
        {panels.length === 0 ? (
          <div className="grid h-full place-items-center p-4 text-center text-xs text-muted">
            Kein {REGION_LABELS[region]}-Panel für diesen Editor.
          </div>
        ) : (
          visible.map((p) => <PanelSlot key={p.id} panel={p} ctx={ctx} />)
        )}
      </div>
    </div>
  );
}

function PanelSlot({ panel, ctx }: { panel: WebPanelDescriptor; ctx: EditorContext }) {
  const Component = panel.Component;
  const props: WebEditorPanelProps = { ctx };
  return <Component {...props} />;
}
